/**
 * UIA-P3-456 — Star Ratings domain weights, single source of truth.
 *
 * The Statura Internal Quality Score (methodology version
 * `STATURA_INTERNAL_V1`) combines four domains into the overall score:
 *
 *   overall = compliance        * 0.30
 *           + residentExperience * 0.33
 *           + staffing           * 0.22
 *           + qualityMeasures    * 0.15
 *
 * Prior to the P3-456 extraction these four values were duplicated across
 * `methodology/page.tsx`, `star-ratings/page.tsx`, `[facilityId]/page.tsx`,
 * and `calculateEnhancedStarRating` in `apps/web/src/lib/supabase/queries.ts`
 * (plus the earlier ad-hoc `calculateStarRating` function in queries.ts).
 * A future weighting change had to update 4+ files in lockstep or displayed
 * weights would diverge from the actual score.
 *
 * Importers are now the only place these literals live. Display surfaces
 * format the weight as a percentage (`weight * 100`); the simulator and
 * server-side scoring use the raw fractions.
 *
 * The values themselves match the Statura-internal methodology and are
 * NOT a direct reproduction of the ACQSC published star-rating weights —
 * the consumer-facing star-rating product remains gated behind the
 * `ACQSC-disclaimer` banner that every consumer renders.
 */

export const STAR_RATINGS_DOMAIN_WEIGHTS = {
  compliance: 0.3,
  residentExperience: 0.33,
  staffing: 0.22,
  qualityMeasures: 0.15,
} as const;

export type StarRatingsDomainKey = keyof typeof STAR_RATINGS_DOMAIN_WEIGHTS;

/**
 * Display-ordered descriptor for each domain. Pages render `name`,
 * `description`, and a percent-formatted `weight`. Server code reads the
 * raw fraction from `STAR_RATINGS_DOMAIN_WEIGHTS[key]`.
 */
export const STAR_RATINGS_DOMAIN_INFO: ReadonlyArray<{
  key: StarRatingsDomainKey;
  name: string;
  weight: number;
  description: string;
}> = [
  {
    key: 'compliance',
    name: 'Compliance',
    weight: STAR_RATINGS_DOMAIN_WEIGHTS.compliance,
    description: 'Self-assessment compliance results with an incident-count penalty.',
  },
  {
    key: 'residentExperience',
    name: 'Resident Experience',
    weight: STAR_RATINGS_DOMAIN_WEIGHTS.residentExperience,
    description: 'Consumer feedback mix and complaint resolution rate.',
  },
  {
    key: 'staffing',
    name: 'Staffing',
    weight: STAR_RATINGS_DOMAIN_WEIGHTS.staffing,
    description: 'RN and total care minutes delivered against configured targets.',
  },
  {
    key: 'qualityMeasures',
    name: 'Quality Measures',
    weight: STAR_RATINGS_DOMAIN_WEIGHTS.qualityMeasures,
    description: 'Operational QI rates benchmarked into a 1.00 to 5.00 domain score.',
  },
];

/** Combine four 1.00–5.00 domain scores into the overall 1.00–5.00 score. */
export function computeStarRatingOverall(domains: {
  compliance: number;
  residentExperience: number;
  staffing: number;
  qualityMeasures: number;
}): number {
  return (
    domains.compliance * STAR_RATINGS_DOMAIN_WEIGHTS.compliance +
    domains.residentExperience * STAR_RATINGS_DOMAIN_WEIGHTS.residentExperience +
    domains.staffing * STAR_RATINGS_DOMAIN_WEIGHTS.staffing +
    domains.qualityMeasures * STAR_RATINGS_DOMAIN_WEIGHTS.qualityMeasures
  );
}

/** Internal simulator comparison points; these are illustrative, not current AIHW benchmarks. */
export const STAR_RATINGS_QI_BENCHMARKS = {
  pressureInjuryRate: 1.2,
  fallsRate: 8.5,
  fallsMajorRate: 1.5,
  medicationErrorRate: 2.0,
  polypharmacyRate: 40,
  restraintRate: 15,
  weightLossRate: 8,
  consumerExperienceRate: 75,
} as const;

export interface StarRatingSimulationSource {
  qiIndicators?: Partial<Record<keyof typeof STAR_RATINGS_QI_BENCHMARKS, number>>;
  rnDeliveryRatio?: number;
  totalDeliveryRatio?: number;
}

export interface StarRatingSimInputs {
  pressureInjuryRate: string;
  fallsRate: string;
  polypharmacyRate: string;
  restraintRate: string;
  rnDeliveryPct: string;
  totalDeliveryPct: string;
}

/** Very simplified scoring for the simulation preview */
export function simulateStarRatingScore(
  currentDomains: { compliance: number; residentExp: number; staffing: number; quality: number },
  sim: StarRatingSimInputs,
  sourceData: StarRatingSimulationSource = {},
): { compliance: number; residentExp: number; staffing: number; quality: number; overall: number } {
  // Quality measures: lower rates = better scores (max 5)
  let qScore = currentDomains.quality;
  if (sim.pressureInjuryRate || sim.fallsRate || sim.polypharmacyRate || sim.restraintRate) {
    const piRate = sim.pressureInjuryRate
      ? parseFloat(sim.pressureInjuryRate)
      : (sourceData.qiIndicators?.pressureInjuryRate ?? 1.0);
    const fRate = sim.fallsRate
      ? parseFloat(sim.fallsRate)
      : (sourceData.qiIndicators?.fallsRate ?? 8.0);
    const polyRate = sim.polypharmacyRate
      ? parseFloat(sim.polypharmacyRate)
      : (sourceData.qiIndicators?.polypharmacyRate ?? 35);
    const restRate = sim.restraintRate
      ? parseFloat(sim.restraintRate)
      : (sourceData.qiIndicators?.restraintRate ?? 10);
    // Simple benchmark scoring: 5 if way below benchmark, 1 if way above
    const piScore = Math.max(
      1,
      // eslint-disable-next-line statura/no-floating-point-money -- unitless star-rating ratio and score, not money
      Math.min(5, 5 - (piRate / STAR_RATINGS_QI_BENCHMARKS.pressureInjuryRate) * 2),
    );
    // eslint-disable-next-line statura/no-floating-point-money -- unitless star-rating ratio and score, not money
    const fScore = Math.max(1, Math.min(5, 5 - (fRate / STAR_RATINGS_QI_BENCHMARKS.fallsRate) * 2));
    const polyScore = Math.max(
      1,
      // eslint-disable-next-line statura/no-floating-point-money -- unitless star-rating ratio and score, not money
      Math.min(5, 5 - (polyRate / STAR_RATINGS_QI_BENCHMARKS.polypharmacyRate) * 2),
    );
    const restScore = Math.max(
      1,
      // eslint-disable-next-line statura/no-floating-point-money -- unitless star-rating ratio and score, not money
      Math.min(5, 5 - (restRate / STAR_RATINGS_QI_BENCHMARKS.restraintRate) * 2),
    );
    qScore = (piScore + fScore + polyScore + restScore) / 4;
  }

  // Staffing: higher delivery % = better score
  let sScore = currentDomains.staffing;
  if (sim.rnDeliveryPct || sim.totalDeliveryPct) {
    const rnPct = sim.rnDeliveryPct
      ? parseFloat(sim.rnDeliveryPct) / 100
      : (sourceData.rnDeliveryRatio ?? 1.0);
    const totalPct = sim.totalDeliveryPct
      ? parseFloat(sim.totalDeliveryPct) / 100
      : (sourceData.totalDeliveryRatio ?? 1.0);
    const rnScore = Math.max(1, Math.min(5, rnPct * 5));
    const totalScore = Math.max(1, Math.min(5, totalPct * 5));
    // eslint-disable-next-line statura/no-floating-point-money -- unitless star-rating ratio and score, not money
    const rawStaffing = rnScore * 0.4 + totalScore * 0.6;
    // Internal simulation rule: cap at 2 if either ratio is below target.
    const eitherBelowTarget = rnPct < 1.0 || totalPct < 1.0;
    sScore = eitherBelowTarget ? Math.min(2, rawStaffing) : rawStaffing;
  }

  // Statura Internal Quality Score weights.
  const overall =
    // eslint-disable-next-line statura/no-floating-point-money -- unitless star-rating ratio and score, not money
    currentDomains.compliance * STAR_RATINGS_DOMAIN_WEIGHTS.compliance +
    // eslint-disable-next-line statura/no-floating-point-money -- unitless star-rating ratio and score, not money
    currentDomains.residentExp * STAR_RATINGS_DOMAIN_WEIGHTS.residentExperience +
    sScore * STAR_RATINGS_DOMAIN_WEIGHTS.staffing +
    qScore * STAR_RATINGS_DOMAIN_WEIGHTS.qualityMeasures;
  return {
    compliance: currentDomains.compliance,
    residentExp: currentDomains.residentExp,
    staffing: sScore,
    quality: qScore,
    overall: Math.max(1, Math.min(5, overall)),
  };
}
